import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Group, LayoutGrid } from 'lucide-react'
import { z } from 'zod/v4'
import { machinesQueryOptions } from '@/api/resources/machines'
import { LoadingSpinner } from '@/components/feedback/loading-spinner'
import { OrganisationFilter } from '@/components/layout/organisation-filter'
import { PageHeader } from '@/components/layout/page-header'
import { LocationMachineGroup } from '@/components/machines/location-machine-group'
import { MachineCard } from '@/components/machines/machine-card'
import { StatusFilter } from '@/components/machines/status-filter'
import { Button } from '@/components/ui/button'
import { useOrganisationFilter } from '@/hooks/use-organisation-filter'
import { groupMachinesByLocation, hasMachineWarning } from '@/lib/machines'
import { useUiStore } from '@/stores/ui-store'

const searchSchema = z.object({
  status: z.string().optional(),
})

export const Route = createFileRoute('/_app/machines/')({
  head: () => ({ meta: [{ title: 'Machines - MyJunea' }] }),
  validateSearch: (search) => searchSchema.parse(search),
  component: MachinesPage,
})

function MachinesPage(): React.ReactElement {
  const { status } = Route.useSearch()
  const navigate = useNavigate()
  const organisationFilter = useOrganisationFilter()
  const machinesGrouped = useUiStore((state) => state.machinesGrouped)
  const setMachinesGrouped = useUiStore((state) => state.setMachinesGrouped)

  const { data, isLoading } = useQuery(machinesQueryOptions({ status, ...organisationFilter }))

  const locationGroups = data !== undefined ? groupMachinesByLocation(data) : []
  const sortedMachines =
    data !== undefined ? [...data].sort((a, b) => a.code.localeCompare(b.code)) : []

  const isEmpty = isLoading
    ? false
    : machinesGrouped
      ? locationGroups.length === 0
      : sortedMachines.length === 0

  function handleStatusChange(value: string | undefined): void {
    void navigate({
      to: '/machines',
      search: value !== undefined ? { status: value } : {},
      resetScroll: false,
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Machines"
        description="Monitor stock levels and machine status across your locations."
        filters={
          <>
            <StatusFilter value={status} onChange={handleStatusChange} />
            <OrganisationFilter />
            <div className="bg-muted flex items-center gap-0.5 rounded-md p-0.5">
              <Button
                size="icon-sm"
                variant={machinesGrouped ? 'default' : 'ghost'}
                onClick={() => setMachinesGrouped(true)}
                aria-label="Group by location"
              >
                <Group />
              </Button>
              <Button
                size="icon-sm"
                variant={!machinesGrouped ? 'default' : 'ghost'}
                onClick={() => setMachinesGrouped(false)}
                aria-label="All machines"
              >
                <LayoutGrid />
              </Button>
            </div>
          </>
        }
      />
      {isLoading && <LoadingSpinner />}
      {!isLoading && isEmpty && (
        <p className="text-muted-foreground py-10 text-center">No machines found.</p>
      )}
      {!isLoading && !isEmpty && machinesGrouped && (
        <div className="space-y-4">
          {locationGroups.map((group) => (
            <LocationMachineGroup
              key={group.groupKey}
              organisationName={group.organisationName}
              locationName={group.locationName}
              machineCount={group.machines.length}
              hasWarning={group.machines.some(hasMachineWarning)}
            >
              {group.machines.map((machine) => (
                <Link
                  key={machine.code}
                  to="/machines/$machineCode"
                  params={{ machineCode: machine.code }}
                  className="focus-visible:ring-ring rounded-md transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:outline-none"
                >
                  <MachineCard
                    name={machine.name}
                    code={machine.code}
                    type={machine.type}
                    typeName={machine.type_name}
                    status={machine.status}
                    statusCategory={machine.status_category}
                    stock={machine.stock}
                    capacity={machine.capacity}
                    emptySlots={machine.empty_slots}
                    className="h-full"
                  />
                </Link>
              ))}
            </LocationMachineGroup>
          ))}
        </div>
      )}
      {!isLoading && !isEmpty && !machinesGrouped && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-5">
          {sortedMachines.map((machine) => (
            <Link
              key={machine.code}
              to="/machines/$machineCode"
              params={{ machineCode: machine.code }}
              className="focus-visible:ring-ring rounded-md transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:outline-none"
            >
              <MachineCard
                name={machine.name}
                code={machine.code}
                type={machine.type}
                typeName={machine.type_name}
                status={machine.status}
                statusCategory={machine.status_category}
                stock={machine.stock}
                capacity={machine.capacity}
                emptySlots={machine.empty_slots}
                className="h-full"
              />
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
